'use client';
import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop';
import { X, Check, ZoomIn, ZoomOut, RotateCcw, AlertCircle } from 'lucide-react';

interface PhotoCropModalProps {
  imageSrc: string;
  onCropComplete: (croppedDataUrl: string) => void;
  onCancel: () => void;
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<string> {
  const image = new window.Image();
  image.src = imageSrc;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  // Output at 800x1000 (4:5)
  canvas.width = 800;
  canvas.height = 1000;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    800,
    1000
  );

  return canvas.toDataURL('image/jpeg', 0.92);
}

export default function PhotoCropModal({ imageSrc, onCropComplete, onCancel }: PhotoCropModalProps) {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = useCallback((c: Point) => setCrop(c), []);
  const onZoomChange = useCallback((z: number) => setZoom(z), []);

  const onCropCompleteCallback = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const croppedUrl = await getCroppedImg(imageSrc, croppedAreaPixels);
      onCropComplete(croppedUrl);
    } catch {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1.0);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
      onClick={e => e.stopPropagation()}
    >
      <div className="w-full max-w-2xl bg-[#1a1a1a] rounded-2xl overflow-hidden flex flex-col max-h-[95vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-white">Crop Your Profile Photo</h2>
            <p className="text-xs text-white/50 mt-0.5">Portrait frame — 4:5 ratio</p>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Cancel"
          >
            <X size={15} className="text-white" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden min-h-0">
          {/* Crop area */}
          <div className="relative flex-1 min-h-[320px] lg:min-h-0 bg-black">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              minZoom={0.5}
              maxZoom={3}
              aspect={4 / 5}
              onCropChange={onCropChange}
              onZoomChange={onZoomChange}
              onCropComplete={onCropCompleteCallback}
              cropShape="rect"
              showGrid={false}
              restrictPosition={false}
              style={{
                containerStyle: { background: '#000' },
                cropAreaStyle: {
                  border: '2px solid rgba(220,38,38,0.8)',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.65)',
                },
              }}
            />

            {/* Oval face guide overlay */}
            <div
              style={{
                position: 'absolute',
                top: '28%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '34%',
                paddingBottom: '40%',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '50%',
                  border: '2px dashed rgba(255,255,255,0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    bottom: '-22px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '10px',
                    color: 'rgba(255,255,255,0.5)',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.02em',
                    pointerEvents: 'none',
                  }}
                >
                  Place your face here
                </span>
              </div>
            </div>
          </div>

          {/* Guidelines panel */}
          <div className="w-full lg:w-64 flex-shrink-0 bg-[#111] border-t lg:border-t-0 lg:border-l border-white/10 overflow-y-auto">
            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-bold text-white/80 uppercase tracking-wider">Profile Photo Guidelines</p>
                <p className="text-xs text-white/60 leading-relaxed">
                  Please center your face clearly inside the frame. Use a clear portrait-style photo showing your face and upper body.
                </p>
              </div>

              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-red-400 flex items-center gap-1.5">
                  <AlertCircle size={11} className="flex-shrink-0" />
                  Avoid:
                </p>
                <ul className="space-y-1">
                  {[
                    'Photos where your face is cut off',
                    'Distant full-body photos',
                    'Group photos',
                    'Sunglasses or face coverings',
                    'Heavy filters or AI-generated images',
                    'Too much blank background',
                    'Side-profile-only photos',
                  ].map((item) => (
                    <li key={item} className="text-xs text-white/50 flex items-start gap-1.5">
                      <span className="text-red-500 mt-0.5 flex-shrink-0">×</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white/5 rounded-xl p-3">
                <p className="text-xs text-white/60 leading-relaxed">
                  <span className="text-white/80 font-semibold">Your photo must clearly show your face</span> to be approved by the JSN team.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Zoom controls + actions */}
        <div className="flex-shrink-0 border-t border-white/10 px-5 py-4 space-y-3">
          {/* Zoom slider */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}
              className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Zoom out"
            >
              <ZoomOut size={13} className="text-white" />
            </button>
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 h-1 accent-red-600 cursor-pointer"
              aria-label="Zoom"
            />
            <button
              onClick={() => setZoom(z => Math.min(3, z + 0.1))}
              className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Zoom in"
            >
              <ZoomIn size={13} className="text-white" />
            </button>
            <button
              onClick={handleReset}
              className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Reset crop"
              title="Reset"
            >
              <RotateCcw size={13} className="text-white" />
            </button>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/15 text-white/80 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isProcessing || !croppedAreaPixels}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-700 hover:bg-red-600 text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                  Processing…
                </span>
              ) : (
                <>
                  <Check size={15} />
                  Save Photo
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
